import { Alert, Button, Card } from '@sonnen/web-ui';
import clsx from 'clsx';
import { useEffect, useMemo } from 'react';
import { useAppContext } from '../../AppContext/AppContext';
import { DataCard } from '../../components/DataCard/DataCard';
import { DataRow } from '../../components/DataRow/DataRow';
import { OrderFlowPage } from '../../components/OrderFlowPage/OrderFlowPage';
import { DataSection, sectionsByFlow } from '../../constants';
import {
  CustomerSalutation,
  EnergyDynamicProductConfigurationWithoutBatteryOptimizationModule,
  useCreateExistingCustomerBatteryOptimizationOrderMutation,
  useCreateExistingCustomerChangingFromFlatXTariffOrderMutation,
  useCreateExistingCustomerOrderMutation,
  useCreateExistingCustomerSelfConsumptionPlusOrderMutation,
  useCreateExistingCustomerSwitchingFromFlatXTariffToBatteryOptimizationOrderMutation,
  useCreateNewCustomerOrderMutation,
} from '../../graphql/generated';
import { useOrderFlowNavigation } from '../../hooks/useOrderFlowNavigation';
import { useTopScrollNavigate } from '../../hooks/useTopScrollNavigate';
import { LocaleKey, useTranslation } from '../../i18n/i18n';
import { useOfferContext } from '../../OfferContext/OfferContext';
import { OrderFlow, TAddress, TDeliveryDetails, TPaymentDetails, TPersonalData } from '../../types';
import {
  getActualBillingAddress,
  getActualDeliveryStartDate,
  getBatteryId,
  getSalutation,
  getSelectedTariffConfiguration,
  getSwitchFromExistingContract,
  localeDateString,
  useSalutations,
} from '../../utils/utils';
import { visitorPersonalDataValidator } from '../../utils/validation';
import {
  CustomerBatteryOptimizationMutationOptions,
  CustomerOrderMutationOptions,
  getBatteryOptimizationOrderInput,
  getCreateCustomerSelfConsumptionPlusOrderInput,
  getOrderMutationInputChangingFromExistingTariff,
  getOrderMutationInputExistingCustomer,
  getOrderMutationInputNewCustomer,
} from './DataReview.helpers';
import styles from './DataReview.module.css';

const DataReview = () => {
  const { t } = useTranslation();
  const navigate = useTopScrollNavigate();
  const salutationOptions = useSalutations();

  const {
    offer,
    productConfigurationsInput: productConfigurationInput,
    workEmail,
  } = useOfferContext();

  const {
    modules,
    user,
    orderFlow,
    formValues,
    setOrderComplete,
    orderComplete,
    selectedSite,
    customBillingAddress,
    setCurrentBackground,
  } = useAppContext();

  useEffect(() => {
    setCurrentBackground();
  }, [setCurrentBackground]);

  const { contractTerminated } = formValues;
  const { previous, next } = useOrderFlowNavigation();
  const switchFromExistingContract = getSwitchFromExistingContract(selectedSite);
  const tariffFlow = orderFlow !== OrderFlow.selfConsumptionPlus;
  const deliveryDetailsFields: (keyof TDeliveryDetails)[] = ['meterId'];

  if (contractTerminated) {
    deliveryDetailsFields.push('confirmedCancellationDate');
  } else {
    deliveryDetailsFields.push('terminatedEnergyProviderName');
    deliveryDetailsFields.push('deliveryStartDate');
  }

  const [createNewCustomerOrderMutation, createNewCustomerOrderMutationResult] =
    useCreateNewCustomerOrderMutation();

  const [createExistingCustomerOrderMutation, createExistingCustomerOrderMutationResult] =
    useCreateExistingCustomerOrderMutation();

  const [
    createExistingCustomerChangingFromFlatXTariffOrderMutation,
    createExistingCustomerChangingFromFlatXTariffOrderMutationResult,
  ] = useCreateExistingCustomerChangingFromFlatXTariffOrderMutation();

  const [
    createExistingCustomerSelfConsumptionPlusOrder,
    createExistingCustomerSelfConsumptionPlusOrderMutationResult,
  ] = useCreateExistingCustomerSelfConsumptionPlusOrderMutation();

  const [
    createExistingCustomerSwitchingFromFlatXTariffToBatteryOptimizationOrderMutation,
    createExistingCustomerSwitchingFromFlatXTariffToBatteryOptimizationOrderMutationResult,
  ] = useCreateExistingCustomerSwitchingFromFlatXTariffToBatteryOptimizationOrderMutation();

  const [
    createExistingCustomerBatteryOptimizationOrderMutation,
    createExistingCustomerBatteryOptimizationOrderMutationResult,
  ] = useCreateExistingCustomerBatteryOptimizationOrderMutation();

  const results = [
    createNewCustomerOrderMutationResult,
    createExistingCustomerOrderMutationResult,
    createExistingCustomerChangingFromFlatXTariffOrderMutationResult,
    createExistingCustomerSelfConsumptionPlusOrderMutationResult,
    createExistingCustomerSwitchingFromFlatXTariffToBatteryOptimizationOrderMutationResult,
    createExistingCustomerBatteryOptimizationOrderMutationResult,
  ];

  const dataNew = createNewCustomerOrderMutationResult.data;
  const dataExisting = createExistingCustomerOrderMutationResult.data;
  const dataSwitchFromFlatX = createExistingCustomerChangingFromFlatXTariffOrderMutationResult.data;
  const dataSelfConsumptionPlus = createExistingCustomerSelfConsumptionPlusOrderMutationResult.data;
  const dataSwitchFromFlatXToBatteryOptimization =
    createExistingCustomerSwitchingFromFlatXTariffToBatteryOptimizationOrderMutationResult.data;
  const dataBatteryOptimization = createExistingCustomerBatteryOptimizationOrderMutationResult.data;

  const loading = results.some((result) => result.loading);
  const hasError = results.some((result) => Boolean(result.error));

  const createOrder = () => {
    if (loading || orderComplete || (!offer && tariffFlow)) return;

    if (user) {
      if (orderFlow === OrderFlow.selfConsumptionPlus) {
        const input = getCreateCustomerSelfConsumptionPlusOrderInput(formValues);
        createExistingCustomerSelfConsumptionPlusOrder({ variables: { input } });
        return;
      }

      const selectedConfiguration =
        orderFlow === OrderFlow.batteryOptimization
          ? offer?.batteryOptimization?.configuration
          : getSelectedTariffConfiguration(offer, modules, orderFlow);

      const options: CustomerBatteryOptimizationMutationOptions = {
        user,
        formValues,
        configuration: selectedConfiguration!,
        batteryId: getBatteryId(selectedSite),
        siteId: selectedSite?.id ?? '',
        workEmail,
      };

      if (orderFlow === OrderFlow.batteryOptimization) {
        const input = getBatteryOptimizationOrderInput(options);
        if (switchFromExistingContract) {
          createExistingCustomerSwitchingFromFlatXTariffToBatteryOptimizationOrderMutation({
            variables: { input },
          });
        } else {
          createExistingCustomerBatteryOptimizationOrderMutation({
            variables: { input },
          });
        }
        return;
      }

      const tariffOptions: CustomerOrderMutationOptions = {
        ...options,
        consumptionEstimated: productConfigurationInput.expectedElectricityConsumptionInKwh || 1,
      };

      if (switchFromExistingContract) {
        const input = getOrderMutationInputChangingFromExistingTariff(tariffOptions);
        createExistingCustomerChangingFromFlatXTariffOrderMutation({ variables: { input } });
      } else {
        const input = getOrderMutationInputExistingCustomer(tariffOptions);
        createExistingCustomerOrderMutation({ variables: { input } });
      }
    } else if (offer) {
      if (!visitorPersonalDataValidator(formValues.personalData)) return;

      const configuration = offer.energyDynamic
        ?.configurationWithoutBatteryOptimizationModule as EnergyDynamicProductConfigurationWithoutBatteryOptimizationModule;

      const input = getOrderMutationInputNewCustomer(
        formValues,
        configuration,
        productConfigurationInput.expectedElectricityConsumptionInKwh || 0,
        customBillingAddress,
        workEmail,
      );
      createNewCustomerOrderMutation({ variables: { input } });
    }
  };

  useEffect(() => {
    if (hasError) return;

    if (
      [
        dataExisting?.createExistingCustomerOrder,
        dataNew?.createNewCustomerOrder,
        dataSwitchFromFlatX?.createExistingCustomerSwitchingFromFlatXTariffOrder,
        dataSelfConsumptionPlus?.createExistingCustomerSelfConsumptionPlusOrder,
        dataSwitchFromFlatXToBatteryOptimization?.createExistingCustomerSwitchingFromFlatXTariffToBatteryOptimizationOrder,
        dataBatteryOptimization?.createExistingCustomerBatteryOptimizationOrder,
      ].some(Boolean)
    ) {
      setOrderComplete(true);
      navigate(next!);
    }
  }, [
    dataExisting,
    dataNew,
    dataSwitchFromFlatX,
    dataSelfConsumptionPlus,
    dataSwitchFromFlatXToBatteryOptimization,
    dataBatteryOptimization,
    hasError,
  ]);

  const personalData = useMemo(() => {
    let values = formValues.personalData;
    const fields: (keyof TPersonalData)[] = ['firstName', 'lastName', 'email', 'phone'];

    if (!user) {
      fields.unshift('salutation');
      const salutation = getSalutation(salutationOptions, formValues.personalData.salutation!);
      values = { ...values, salutation: salutation?.label as CustomerSalutation };
    }
    if (values.companyName) fields.unshift('companyName');
    return { fields, values };
  }, [user]);

  const showErrorNotification = hasError && !loading;

  const actualBillingAddress = getActualBillingAddress(
    formValues,
    customBillingAddress || Boolean(user),
  );

  const title: LocaleKey = tariffFlow ? 'review.title' : 'review.selfConsumptionPlusTitle';
  const orderNowLabel: LocaleKey = tariffFlow
    ? 'review.orderNow'
    : 'review.activateSelfConsumptionPlusNow';

  const sections = sectionsByFlow[orderFlow!];

  return (
    <OrderFlowPage dataTestId="data-review" title={t(title)} className={styles.dataReview}>
      <DataCard<TPersonalData>
        fields={personalData.fields}
        i18nScope="data.personalInfo"
        dataTestId="personal-info"
        source={personalData.values}
      />

      <DataCard<TAddress>
        fields={['streetName', 'streetNumber', 'postalCode', 'city']}
        dataTestId="delivery-address"
        i18nScope="data.address"
        source={formValues.deliveryAddress}
        name="deliveryAddress"
      />

      <DataCard<TAddress>
        fields={['streetName', 'streetNumber', 'postalCode', 'city']}
        dataTestId="billing-address"
        i18nScope="data.address"
        source={actualBillingAddress}
        name="billingAddress"
      />

      {!switchFromExistingContract && sections.has(DataSection.DeliveryDetails) && (
        <DataCard<TDeliveryDetails>
          fields={deliveryDetailsFields}
          i18nScope="review.deliveryDetails"
          dataTestId="delivery-details"
          source={formValues}
        >
          {!user && formValues.providerChangeReason && (
            <DataRow
              label={t('review.deliveryDetails.providerChangeReason')}
              value={t(`data.providerChangeReason.${formValues.providerChangeReason}.label`)}
              dataTestId="reason-for-change"
            />
          )}

          {contractTerminated && (
            <DataRow
              label={t('review.deliveryDetails.deliveryStartDate')}
              value={localeDateString(getActualDeliveryStartDate(formValues))}
              dataTestId="projected-delivery-start-date"
            />
          )}
        </DataCard>
      )}

      {sections.has(DataSection.PaymentDetails) && (
        <DataCard<TPaymentDetails>
          fields={['iban', 'accountOwner']}
          i18nScope="data.paymentDetails"
          dataTestId="payment-details"
          source={formValues}
        />
      )}

      <Card paddingSize="md">
        {(loading || orderComplete || showErrorNotification) && (
          <div className={styles.notifications}>
            {loading && (
              <Alert variant="info" dataTestId="loading-notification">
                {t('review.placingOrder')}
              </Alert>
            )}
            {orderComplete && (
              <Alert variant="success" dataTestId="order-complete-notification">
                {t('review.orderComplete')}
              </Alert>
            )}
            {showErrorNotification && (
              <Alert variant="error" dataTestId="error-notification">
                {t('review.orderError')}
              </Alert>
            )}
          </div>
        )}

        <div className={clsx(styles.buttons)}>
          <Button
            className={styles.editBtn}
            variant="secondary"
            label={t('review.editData')}
            onClick={() => navigate(previous!, { state: 'data-review' })}
            dataTestId="edit-data-btn"
            disabled={loading}
            expanded
          />

          <Button
            className={styles.orderBtn}
            size="large"
            label={t(orderNowLabel)}
            onClick={createOrder}
            loading={loading}
            disabled={orderComplete}
            dataTestId="purchase"
            expanded
          />
        </div>
      </Card>
    </OrderFlowPage>
  );
};

export default DataReview;
